/**
 * Get the appropriate payee label based on shipment mode (fallback to shipment_type)
 * Rule: Import/Import-Event/Brokerage → Consignee; Export/Export-Event → Shipper
 * @param {Object} shipment - Shipment details object
 * @returns {string} The label to display
 */
export const getCostAnalysisPayeeLabel = (shipment) => {
  // Check shipment_mode first, fallback to shipment_type if mode is null/empty
  const shipmentMode = String(shipment?.shipment_mode || "").toLowerCase();
  const shipmentType = String(shipment?.shipment_type || "").toLowerCase();
  const modeOrType = shipmentMode || shipmentType;

  if (!modeOrType) return "Payee";

  if (modeOrType.includes("import") || modeOrType.includes("brokerage")) {
    // Import, Import-Event, or Brokerage → Consignee
    return "Consignee";
  } else if (modeOrType.includes("export")) {
    // Export or Export-Event → Shipper
    return "Shipper";
  }

  return "Payee";
};

/**
 * Get the appropriate payee value based on shipment mode (fallback to shipment_type)
 * Rule: Import/Import-Event/Brokerage → receiver_name (Consignee)
 *       Export/Export-Event → shipper_name (Shipper)
 * For non-operational (no JO): use fallback (payee from request)
 *
 * @param {Object} shipment - Shipment details object containing receiver_name, shipper_name, shipment_mode, shipment_type
 * @param {string} fallback - Fallback value (payee from request) for non-operational
 * @returns {string} The payee value to display
 */
export const getCostAnalysisPayeeValue = (shipment, fallback = "") => {
  // If no shipment details, use fallback (non-operational case)
  if (!shipment) {
    return fallback || "";
  }

  // Check shipment_mode first, fallback to shipment_type if mode is null/empty
  const shipmentMode = String(shipment.shipment_mode || "").toLowerCase();
  const shipmentType = String(shipment.shipment_type || "").toLowerCase();
  const modeOrType = shipmentMode || shipmentType;

  // Operational case: determine based on shipment mode/type
  if (modeOrType.includes("import") || modeOrType.includes("brokerage")) {
    // Import, Import-Event, or Brokerage -> use receiver_name (Consignee)
    return shipment.receiver_name || fallback || "";
  } else if (modeOrType.includes("export")) {
    // Export or Export-Event -> use shipper_name (Shipper)
    return shipment.shipper_name || fallback || "";
  }

  // If shipment exists but mode/type is unclear, use fallback
  return fallback || "";
};
